import {
  type Lead,
  type LeadStatus,
  type LeadConversionMetrics,
  type LeadOutreachHistoryEntry,
  type LeadTemplate,
} from "./lead-types.ts";
import { BUILT_IN_LEAD_TEMPLATES } from "./lead-templates.ts";

const LEADS_STORAGE_KEY = "currencycore_admin_leads_v1";
const LEAD_HISTORY_STORAGE_KEY = "currencycore_admin_lead_history_v1";
const CUSTOM_LEAD_TEMPLATES_KEY = "currencycore_admin_lead_custom_templates_v1";

const SEED_LEADS: Lead[] = [
  {
    id: "lead-seed-1",
    company: "SmartSpend Expense",
    website: "https://smartspend.example.com",
    email: "founder@smartspend.example.com",
    contactName: "Alex",
    category: "expense_management",
    status: "new",
    followUpCount: 0,
    useCase: "Multi-currency receipt reconciliation & corporate cards",
    painPoint: "Volatile FX spreads and delayed daily average rates",
    notes: "Spotted on Product Hunt. Mobile app for international business travelers.",
    lastContactedAt: null,
    convertedAt: null,
    createdAt: "2026-09-18T10:00:00.000Z",
    updatedAt: "2026-09-18T10:00:00.000Z",
  },
  {
    id: "lead-seed-2",
    company: "DevFlow SaaS",
    website: "https://devflow.example.io",
    email: "team@devflow.example.io",
    contactName: "Sarah",
    category: "saas_pricing",
    status: "new",
    followUpCount: 0,
    useCase: "Localized pricing with Purchasing Power Parity",
    painPoint: "High bounce rate from Asian and European visitors due to USD-only checkout",
    notes: "Developer tooling SaaS with growing international traffic.",
    lastContactedAt: null,
    convertedAt: null,
    createdAt: "2026-09-19T14:30:00.000Z",
    updatedAt: "2026-09-19T14:30:00.000Z",
  },
  {
    id: "lead-seed-3",
    company: "GlobalPay Invoicing",
    website: "https://globalpay.example.co",
    email: "ops@globalpay.example.co",
    contactName: "Marcus",
    category: "invoicing_payroll",
    status: "new",
    followUpCount: 0,
    useCase: "Cross-border contractor invoice settlement",
    painPoint: "Disputes over exchange rate discrepancies between contractor and client",
    notes: "Contractor management platform expanding across LATAM and Europe.",
    lastContactedAt: null,
    convertedAt: null,
    createdAt: "2026-09-20T09:15:00.000Z",
    updatedAt: "2026-09-20T09:15:00.000Z",
  },
];

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

/**
 * Fetch all leads from local storage, initializing with seed data if empty.
 */
export function getLeads(): Lead[] {
  if (!isBrowser()) return SEED_LEADS;
  try {
    const raw = localStorage.getItem(LEADS_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(SEED_LEADS));
      return SEED_LEADS;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return SEED_LEADS;
    return parsed;
  } catch (e) {
    console.error("Failed to read leads from localStorage:", e);
    return SEED_LEADS;
  }
}

/**
 * Find single lead by ID.
 */
export function getLead(id: string): Lead | undefined {
  return getLeads().find((l) => l.id === id);
}

/**
 * Create or update a lead.
 */
export function saveLead(
  leadInput: Omit<Lead, "id" | "createdAt" | "updatedAt"> & { id?: string },
): Lead {
  const leads = getLeads();
  const now = new Date().toISOString();

  if (leadInput.id) {
    const idx = leads.findIndex((l) => l.id === leadInput.id);
    if (idx !== -1) {
      const existing = leads[idx]!;
      const updated: Lead = {
        ...existing,
        ...leadInput,
        id: existing.id,
        updatedAt: now,
        convertedAt:
          leadInput.status === "converted" && !existing.convertedAt
            ? now
            : leadInput.status !== "converted"
              ? null
              : existing.convertedAt,
      };
      leads[idx] = updated;
      if (isBrowser()) {
        localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(leads));
      }
      return updated;
    }
  }

  // Create new lead
  const newLead: Lead = {
    ...leadInput,
    id: `lead_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    followUpCount: leadInput.followUpCount ?? 0,
    status: leadInput.status ?? "new",
    lastContactedAt: leadInput.lastContactedAt ?? null,
    convertedAt: leadInput.status === "converted" ? now : null,
    createdAt: now,
    updatedAt: now,
  };

  leads.unshift(newLead);
  if (isBrowser()) {
    localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(leads));
  }
  return newLead;
}

/**
 * Delete a lead by ID.
 */
export function deleteLead(id: string): void {
  const leads = getLeads().filter((l) => l.id !== id);
  if (isBrowser()) {
    localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(leads));
  }
}

/**
 * Update lead status directly (e.g. from table quick action).
 */
export function updateLeadStatus(id: string, status: LeadStatus): Lead | null {
  const lead = getLead(id);
  if (!lead) return null;
  return saveLead({ ...lead, status });
}

/**
 * Record sent outreach email to a lead.
 * Updates lead lastContactedAt, increments followUpCount, and advances status from 'new' to 'contacted'.
 */
export function recordLeadEmailSent(
  leadId: string | undefined,
  entry: Omit<LeadOutreachHistoryEntry, "id" | "sentAt">,
): LeadOutreachHistoryEntry {
  const now = new Date().toISOString();
  const historyEntry: LeadOutreachHistoryEntry = {
    ...entry,
    id: `hist_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    sentAt: now,
  };

  // 1. Update history
  const history = getLeadHistory();
  history.unshift(historyEntry);
  if (isBrowser()) {
    localStorage.setItem(LEAD_HISTORY_STORAGE_KEY, JSON.stringify(history.slice(0, 500)));
  }

  // 2. If leadId provided, advance lead status
  if (leadId) {
    const lead = getLead(leadId);
    if (lead) {
      const nextStatus: LeadStatus = lead.status === "new" ? "contacted" : lead.status;
      saveLead({
        ...lead,
        status: nextStatus,
        followUpCount: (lead.followUpCount ?? 0) + 1,
        lastContactedAt: now,
      });
    }
  }

  return historyEntry;
}

/**
 * Get all sent history logs.
 */
export function getLeadHistory(): LeadOutreachHistoryEntry[] {
  if (!isBrowser()) return [];
  try {
    const raw = localStorage.getItem(LEAD_HISTORY_STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to read lead history:", e);
    return [];
  }
}

/**
 * Calculate conversion metrics and funnel statistics.
 */
export function calculateConversionMetrics(): LeadConversionMetrics {
  const leads = getLeads();
  const totalLeads = leads.length;

  if (totalLeads === 0) {
    return {
      totalLeads: 0,
      contactedCount: 0,
      repliedCount: 0,
      convertedCount: 0,
      notInterestedCount: 0,
      conversionRate: 0,
      contactedRate: 0,
      responseRate: 0,
    };
  }

  let contactedCount = 0;
  let repliedCount = 0;
  let convertedCount = 0;
  let notInterestedCount = 0;

  for (const l of leads) {
    if (l.status === "converted") {
      convertedCount++;
      contactedCount++;
    } else if (l.status === "replied") {
      repliedCount++;
      contactedCount++;
    } else if (l.status === "contacted" || l.status === "follow_up_needed" || (l.followUpCount ?? 0) > 0) {
      contactedCount++;
    } else if (l.status === "not_interested") {
      notInterestedCount++;
      if ((l.followUpCount ?? 0) > 0) contactedCount++;
    }
  }

  const conversionRate = totalLeads > 0 ? (convertedCount / totalLeads) * 100 : 0;
  const contactedRate = totalLeads > 0 ? (contactedCount / totalLeads) * 100 : 0;
  const responseRate = contactedCount > 0 ? ((repliedCount + convertedCount) / contactedCount) * 100 : 0;

  return {
    totalLeads,
    contactedCount,
    repliedCount,
    convertedCount,
    notInterestedCount,
    conversionRate: Math.round(conversionRate * 10) / 10,
    contactedRate: Math.round(contactedRate * 10) / 10,
    responseRate: Math.round(responseRate * 10) / 10,
  };
}

/**
 * Get all available lead templates (built-in + user custom).
 */
export function getAllLeadTemplates(): LeadTemplate[] {
  const builtIn = BUILT_IN_LEAD_TEMPLATES;
  if (!isBrowser()) return builtIn;
  try {
    const raw = localStorage.getItem(CUSTOM_LEAD_TEMPLATES_KEY);
    const custom: LeadTemplate[] = raw ? JSON.parse(raw) : [];
    return [...builtIn, ...custom];
  } catch (e) {
    console.error("Failed to read custom lead templates:", e);
    return builtIn;
  }
}

/**
 * Save custom lead template.
 */
export function saveCustomLeadTemplate(
  tpl: Omit<LeadTemplate, "id" | "isBuiltIn"> & { id?: string },
): LeadTemplate {
  const raw = localStorage.getItem(CUSTOM_LEAD_TEMPLATES_KEY);
  const custom: LeadTemplate[] = raw ? JSON.parse(raw) : [];

  if (tpl.id) {
    const idx = custom.findIndex((t) => t.id === tpl.id);
    if (idx !== -1) {
      const updated: LeadTemplate = {
        ...tpl,
        id: tpl.id,
        isBuiltIn: false,
      };
      custom[idx] = updated;
      localStorage.setItem(CUSTOM_LEAD_TEMPLATES_KEY, JSON.stringify(custom));
      return updated;
    }
  }

  const newTpl: LeadTemplate = {
    ...tpl,
    id: `lead_tpl_${Date.now()}`,
    isBuiltIn: false,
  };
  custom.push(newTpl);
  localStorage.setItem(CUSTOM_LEAD_TEMPLATES_KEY, JSON.stringify(custom));
  return newTpl;
}

/**
 * Delete custom lead template.
 */
export function deleteCustomLeadTemplate(id: string): void {
  const raw = localStorage.getItem(CUSTOM_LEAD_TEMPLATES_KEY);
  const custom: LeadTemplate[] = raw ? JSON.parse(raw) : [];
  const filtered = custom.filter((t) => t.id !== id);
  localStorage.setItem(CUSTOM_LEAD_TEMPLATES_KEY, JSON.stringify(filtered));
}

/**
 * Export all leads to CSV string.
 */
export function exportLeadsToCSV(): string {
  const leads = getLeads();
  const headers = [
    "Company",
    "Website",
    "Email",
    "Contact Name",
    "Category",
    "Status",
    "Follow-up Count",
    "Use Case",
    "Pain Point",
    "Notes",
    "Last Contacted",
    "Converted At",
    "Created At",
  ];

  const rows = leads.map((l) => [
    `"${(l.company || "").replace(/"/g, '""')}"`,
    `"${(l.website || "").replace(/"/g, '""')}"`,
    `"${(l.email || "").replace(/"/g, '""')}"`,
    `"${(l.contactName || "").replace(/"/g, '""')}"`,
    `"${l.category || "other"}"`,
    `"${l.status || "new"}"`,
    l.followUpCount ?? 0,
    `"${(l.useCase || "").replace(/"/g, '""')}"`,
    `"${(l.painPoint || "").replace(/"/g, '""')}"`,
    `"${(l.notes || "").replace(/"/g, '""')}"`,
    `"${l.lastContactedAt || ""}"`,
    `"${l.convertedAt || ""}"`,
    `"${l.createdAt || ""}"`,
  ]);

  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

/**
 * Trigger CSV download in browser.
 */
export function downloadLeadsCSV(): void {
  const csv = exportLeadsToCSV();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `currencycore_leads_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Import leads from a CSV string.
 */
export function importLeadsFromCSV(csvText: string): { importedCount: number; errors: string[] } {
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    return { importedCount: 0, errors: ["CSV file contains no data rows."] };
  }

  // Parse header
  const headerLine = lines[0]!;
  const headerCols = parseCsvLine(headerLine).map((h) => h.toLowerCase().trim());

  const companyIdx = headerCols.findIndex((h) => h.includes("company") || h.includes("name"));
  const emailIdx = headerCols.findIndex((h) => h.includes("email"));
  const websiteIdx = headerCols.findIndex((h) => h.includes("web") || h.includes("url") || h.includes("site"));
  const contactIdx = headerCols.findIndex((h) => h.includes("contact") || h.includes("person") || h.includes("founder"));
  const categoryIdx = headerCols.findIndex((h) => h.includes("cat") || h.includes("indus") || h.includes("type"));
  const useCaseIdx = headerCols.findIndex((h) => h.includes("use") || h.includes("focus"));
  const painPointIdx = headerCols.findIndex((h) => h.includes("pain") || h.includes("problem"));
  const notesIdx = headerCols.findIndex((h) => h.includes("note"));

  if (emailIdx === -1 && companyIdx === -1) {
    return { importedCount: 0, errors: ["Could not detect 'Company' or 'Email' column in CSV header."] };
  }

  let importedCount = 0;
  const errors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const rawLine = lines[i]!;
    const cols = parseCsvLine(rawLine);
    const email = (emailIdx !== -1 ? cols[emailIdx] : "")?.trim() || "";
    const company = (companyIdx !== -1 ? cols[companyIdx] : "")?.trim() || "Prospective Company";

    if (!email && !company) continue;

    try {
      saveLead({
        company: company || "Unnamed Lead",
        email: email || "",
        website: (websiteIdx !== -1 ? cols[websiteIdx] : "")?.trim() || "",
        contactName: (contactIdx !== -1 ? cols[contactIdx] : "")?.trim() || "",
        category: "other",
        status: "new",
        followUpCount: 0,
        useCase: (useCaseIdx !== -1 ? cols[useCaseIdx] : "")?.trim() || "",
        painPoint: (painPointIdx !== -1 ? cols[painPointIdx] : "")?.trim() || "",
        notes: (notesIdx !== -1 ? cols[notesIdx] : "")?.trim() || "",
      });
      importedCount++;
    } catch (err) {
      errors.push(`Row ${i + 1}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  return { importedCount, errors };
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i]!;
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}
